import React, { useEffect, useRef, useState } from "react";

// pdf.js loaded on demand, same pattern as OfferPdfViewer.
async function loadPdfjs() {
  const pdfjsLib = await import("pdfjs-dist");
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
  ).toString();
  return pdfjsLib;
}

export type EsignField = {
  id: string; type: "signature" | "date"; page: number;
  x: number; y: number; width: number; height: number; role: string;
};
export type EsignRole = { role: string; routing_step: number };

const FIELD_DEFAULT_SIZE: Record<string, { width: number; height: number }> = {
  signature: { width: 0.28, height: 0.06 },
  date: { width: 0.16, height: 0.035 },
};

/**
 * Renders an uploaded (not-yet-submitted) PDF page by page and lets the
 * sender click to place Signature/Date fields, each assigned to a signer
 * role. Coordinates are stored as page-relative fractions (0-1) with a
 * top-left origin — the exact contract document_pdf.apply_field_overlays
 * expects server-side.
 *
 * The rendering container is owned entirely by the imperative pdf.js code
 * (innerHTML/appendChild) — React never renders children into it, matching
 * the fix applied to OfferPdfViewer for the same class of DOM-ownership
 * conflict ("removeChild: node is not a child of this node").
 */
export function EsignFieldEditor({
  file, initialRoles, onSave, onCancel,
}: {
  file: File;
  initialRoles?: EsignRole[];
  onSave: (fields: EsignField[], roles: EsignRole[]) => void;
  onCancel: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [pageSizes, setPageSizes] = useState<{ width: number; height: number }[]>([]);
  const [roles, setRoles] = useState<EsignRole[]>(
    initialRoles && initialRoles.length ? initialRoles : [{ role: "Signer 1", routing_step: 0 }],
  );
  const [activeRole, setActiveRole] = useState(0);
  const [activeType, setActiveType] = useState<"signature" | "date">("signature");
  const [fields, setFields] = useState<EsignField[]>([]);
  const nextId = useRef(1);

  // Click handler needs the CURRENT activeRole/activeType, but is registered
  // once when the PDF renders — a ref sidesteps re-registering listeners
  // (and re-rendering canvases) on every toolbar change.
  const placeFieldRef = useRef<(page: number, x: number, y: number) => void>(() => {});
  useEffect(() => {
    placeFieldRef.current = (page, x, y) => {
      const size = FIELD_DEFAULT_SIZE[activeType];
      const id = `f${nextId.current++}`;
      setFields(prev => [...prev, {
        id, type: activeType, page,
        x: Math.min(Math.max(x, 0), 1 - size.width),
        y: Math.min(Math.max(y, 0), 1 - size.height),
        width: size.width, height: size.height,
        role: roles[activeRole]?.role || roles[0].role,
      }]);
    };
  }, [activeType, activeRole, roles]);

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    (async () => {
      try {
        const pdfjsLib = await loadPdfjs();
        const buf = await file.arrayBuffer();
        const doc = await pdfjsLib.getDocument({ data: buf }).promise;
        if (cancelled) return;
        container.innerHTML = "";
        const width = container.clientWidth - 2;
        const ratio = window.devicePixelRatio || 1;
        const sizes: { width: number; height: number }[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const scale = width / base.width;
          const viewport = page.getViewport({ scale });

          const wrapper = document.createElement("div");
          wrapper.style.position = "relative";
          wrapper.style.width = `${viewport.width}px`;
          wrapper.style.height = `${viewport.height}px`;
          wrapper.style.borderBottom = "1px solid #e2e8f0";
          wrapper.style.cursor = "crosshair";

          const canvas = document.createElement("canvas");
          canvas.width = viewport.width * ratio;
          canvas.height = viewport.height * ratio;
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;
          canvas.style.display = "block";
          const ctx = canvas.getContext("2d")!;
          ctx.scale(ratio, ratio);
          await page.render({ canvasContext: ctx, viewport, canvas }).promise;
          if (cancelled) return;

          wrapper.appendChild(canvas);
          const pageNum = i;
          wrapper.addEventListener("click", (e) => {
            const rect = wrapper.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width;
            const y = (e.clientY - rect.top) / rect.height;
            placeFieldRef.current(pageNum, x, y);
          });
          container.appendChild(wrapper);
          sizes.push({ width: viewport.width, height: viewport.height });
        }
        setPageSizes(sizes);
        setState("ready");
      } catch {
        if (!cancelled) setState("error");
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  function removeField(id: string) {
    setFields(prev => prev.filter(f => f.id !== id));
  }

  function addRole() {
    setRoles(prev => [...prev, { role: `Signer ${prev.length + 1}`, routing_step: prev.length }]);
  }
  function removeRole(idx: number) {
    if (roles.length <= 1) return;
    const removedName = roles[idx].role;
    setRoles(prev => prev.filter((_, i) => i !== idx));
    setFields(prev => prev.filter(f => f.role !== removedName));
    setActiveRole(0);
  }
  function updateRoleName(idx: number, name: string) {
    const oldName = roles[idx].role;
    setRoles(prev => prev.map((r, i) => i === idx ? { ...r, role: name } : r));
    setFields(prev => prev.map(f => f.role === oldName ? { ...f, role: name } : f));
  }
  function updateRoleStep(idx: number, step: number) {
    setRoles(prev => prev.map((r, i) => i === idx ? { ...r, routing_step: Math.max(0, step) } : r));
  }

  const roleNamesValid = roles.every(r => r.role.trim().length > 0);
  const rolesUnique = new Set(roles.map(r => r.role.trim())).size === roles.length;
  const everyRoleHasAField = roles.every(r => fields.some(f => f.role === r.role));
  const canSave = fields.length > 0 && roleNamesValid && rolesUnique && everyRoleHasAField;

  let saveHint = "";
  if (!roleNamesValid) saveHint = "Every signer role needs a name.";
  else if (!rolesUnique) saveHint = "Role names must be unique.";
  else if (fields.length === 0) saveHint = "Click the document to place at least one field.";
  else if (!everyRoleHasAField) saveHint = "Every signer role needs at least one field.";

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, padding: 12, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0" }}>
        {roles.map((r, idx) => (
          <div key={idx} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 6,
            border: activeRole === idx ? "2px solid #1e3a8a" : "1px solid #cbd5e1", background: "white",
          }}>
            <input
              value={r.role}
              onChange={e => updateRoleName(idx, e.target.value)}
              onFocus={() => setActiveRole(idx)}
              style={{ border: "none", outline: "none", fontSize: 12, fontWeight: 600, width: 90, color: "#1e293b" }}
            />
            <input
              type="number" min={0} value={r.routing_step}
              onChange={e => updateRoleStep(idx, Number(e.target.value))}
              title="Routing step — same number signs in parallel; a higher number is notified only once every lower number has signed"
              style={{ width: 36, fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 4, color: "#475569" }}
            />
            {roles.length > 1 && (
              <button type="button" onClick={() => removeRole(idx)}
                style={{ border: "none", background: "none", color: "#dc2626", cursor: "pointer", fontSize: 12, padding: 0 }}>
                ✕
              </button>
            )}
          </div>
        ))}
        <button type="button" onClick={addRole}
          style={{ padding: "4px 10px", borderRadius: 6, border: "1px dashed #94a3b8", background: "white", fontSize: 12, color: "#475569", cursor: "pointer" }}>
          + Add signer role
        </button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8, alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "#64748b" }}>Click the document to place:</span>
        <button type="button" onClick={() => setActiveType("signature")}
          style={{
            padding: "6px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer",
            border: activeType === "signature" ? "2px solid #1e3a8a" : "1px solid #cbd5e1",
            background: activeType === "signature" ? "#eff6ff" : "white", color: "#1e293b",
          }}>
          ✍️ Signature
        </button>
        <button type="button" onClick={() => setActiveType("date")}
          style={{
            padding: "6px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer",
            border: activeType === "date" ? "2px solid #0d9488" : "1px solid #cbd5e1",
            background: activeType === "date" ? "#f0fdfa" : "white", color: "#1e293b",
          }}>
          📅 Date
        </button>
        <span style={{ fontSize: 12, color: "#64748b" }}>for <strong>{roles[activeRole]?.role}</strong></span>
      </div>

      <div style={{ position: "relative", maxHeight: 560, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 10, background: "#f1f5f9" }}>
        <div ref={containerRef} />
        {state === "loading" && (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#64748b", fontSize: 13 }}>Loading document…</div>
        )}
        {state === "error" && (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#dc2626", fontSize: 13 }}>Couldn't load this PDF.</div>
        )}
        {state === "ready" && pageSizes.map((size, pageIdx) => {
          const page = pageIdx + 1;
          const pageTop = pageSizes.slice(0, pageIdx).reduce((sum, s) => sum + s.height + 1, 0);
          return fields.filter(f => f.page === page).map(f => (
            <div key={f.id} style={{
              position: "absolute",
              left: f.x * size.width, top: pageTop + f.y * size.height,
              width: f.width * size.width, height: f.height * size.height,
              border: `2px solid ${f.type === "signature" ? "#1e3a8a" : "#0d9488"}`,
              background: f.type === "signature" ? "rgba(30,58,138,0.08)" : "rgba(13,148,136,0.08)",
              borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "0 4px", fontSize: 9, color: f.type === "signature" ? "#1e3a8a" : "#0d9488", fontWeight: 700,
              pointerEvents: "none",
            }}>
              <span>{f.type === "signature" ? "Sign" : "Date"} · {f.role}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); removeField(f.id); }}
                style={{ pointerEvents: "auto", border: "none", background: "rgba(255,255,255,0.9)", borderRadius: 3, cursor: "pointer", fontSize: 9, padding: "0 3px" }}>
                ✕
              </button>
            </div>
          ));
        })}
      </div>

      {fields.length === 0 && state === "ready" && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "#94a3b8" }}>No fields placed yet — click the document above to add one.</p>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 16 }}>
        <span style={{ fontSize: 11, color: "#dc2626" }}>{!canSave ? saveHint : ""}</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={onCancel}
            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "white", fontSize: 13, color: "#1e293b", cursor: "pointer" }}>
            Cancel
          </button>
          <button type="button" onClick={() => onSave(fields, roles)} disabled={!canSave}
            style={{
              padding: "8px 16px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 600,
              background: !canSave ? "#94a3b8" : "#1e3a8a", color: "white",
              cursor: !canSave ? "not-allowed" : "pointer",
            }}>
            Save Template
          </button>
        </div>
      </div>
    </div>
  );
}
